
import { Users, Target, Filter, Clock, LineChart, Zap, BookOpen, UserCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Recruiters = () => {
  const { t } = useTranslation('recruiters');

  const features = [
    {
      title: t('features.list.needs.title'),
      description: t('features.list.needs.description'),
      icon: <Target className="w-6 h-6 text-brand-orange" />
    },
    {
      title: t('features.list.time.title'),
      description: t('features.list.time.description'),
      icon: <Clock className="w-6 h-6 text-brand-orange" />
    },
    {
      title: t('features.list.applications.title'),
      description: t('features.list.applications.description'),
      icon: <Filter className="w-6 h-6 text-brand-orange" />
    },
    {
      title: t('features.list.realtime.title'),
      description: t('features.list.realtime.description'),
      icon: <LineChart className="w-6 h-6 text-brand-orange" />
    }
  ];

  const interimFeatures = [
    {
      title: t('interim.features.0.title'),
      description: t('interim.features.0.description'),
      icon: <Zap className="w-6 h-6 text-brand-blue" />
    },
    {
      title: t('interim.features.1.title'),
      description: t('interim.features.1.description'),
      icon: <BookOpen className="w-6 h-6 text-brand-blue" />
    },
    {
      title: t('interim.features.2.title'),
      description: t('interim.features.2.description'),
      icon: <UserCheck className="w-6 h-6 text-brand-blue" />
    },
    {
      title: t('interim.features.3.title'),
      description: t('interim.features.3.description'),
      icon: <Clock className="w-6 h-6 text-brand-blue" />
    }
  ];

  const vaeFeatures = [
    {
      title: t('vae.features.skills.title'),
      description: t('vae.features.skills.description')
    },
    {
      title: t('vae.features.validation.title'),
      description: t('vae.features.validation.description')
    },
    {
      title: t('vae.features.process.title'),
      description: t('vae.features.process.description')
    }
  ];

  return (
    <div className="bg-background">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(45rem_50rem_at_top,theme(colors.brand.orange/8),transparent)] dark:bg-[radial-gradient(45rem_50rem_at_top,theme(colors.brand.orange/4),transparent)]" />
          <div className="absolute inset-0 bg-[radial-gradient(45rem_50rem_at_top_right,theme(colors.brand.blue/5),transparent)] dark:bg-[radial-gradient(45rem_50rem_at_top_right,theme(colors.brand.blue/3),transparent)]" />
        </div>
        
        <div className="container-custom relative z-10 py-24">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-blue/10 dark:bg-brand-blue/5 text-brand-blue dark:text-brand-orange mb-8">
              <Users className="w-5 h-5" />
              <span className="text-sm font-medium">{t('badge')}</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-brand-blue to-brand-orange leading-[1.2] pb-4">
              {t('title')}
            </h1>
            
            <p className="text-xl text-muted-foreground mb-12 max-w-2xl mx-auto">
              {t('description')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact?type=demo" 
                className="inline-flex items-center px-6 py-3 rounded-lg bg-gradient-to-r from-brand-blue to-brand-orange text-white hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                {t('cta.demo')}
              </Link>
              <Link
                to="/contact" 
                className="inline-flex items-center px-6 py-3 rounded-lg border-2 border-brand-orange text-brand-orange hover:bg-brand-orange hover:text-white transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                {t('cta.contact')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-card">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-20">
              <h2 className="text-4xl sm:text-5xl font-bold text-center bg-clip-text text-transparent bg-gradient-to-r from-brand-blue to-brand-orange mb-8">
                {t('features.title')}
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                {t('features.subtitle')}
              </p>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-8 lg:gap-12">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="group bg-background rounded-xl p-8 hover:shadow-xl transition-all duration-300 border border-border hover:-translate-y-1"
                >
                  <div className="flex items-start gap-6">
                    <div className="p-3 bg-brand-orange/10 dark:bg-brand-orange/5 rounded-xl group-hover:scale-110 transition-transform">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-medium text-brand-blue dark:text-brand-orange group-hover:text-[#8B5CF6] transition-colors">
                        {feature.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Interim Section */}
      <section className="py-24 bg-gradient-to-br from-background to-card/50">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-bold text-center bg-clip-text text-transparent bg-gradient-to-r from-brand-blue to-brand-orange mb-6">
                {t('interim.title')}
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                {t('interim.subtitle')}
              </p>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-8 lg:gap-12">
              {interimFeatures.map((feature, index) => (
                <div 
                  key={index} 
                  className="group bg-background/80 backdrop-blur-sm rounded-xl p-8 hover:shadow-xl transition-all duration-300 border border-border hover:-translate-y-1"
                >
                  <div className="flex items-start gap-6">
                    <div className="p-3 bg-brand-blue/10 dark:bg-brand-blue/5 rounded-xl group-hover:scale-110 transition-transform">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-medium text-brand-blue dark:text-brand-orange group-hover:text-[#8B5CF6] transition-colors">
                        {feature.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed mt-2">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* VAE Section */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-20">
              <h2 className="text-4xl sm:text-5xl font-bold text-center bg-clip-text text-transparent bg-gradient-to-r from-brand-blue to-brand-orange mb-8">
                {t('vae.title')}
              </h2>
            </div>
            
            <div className="grid sm:grid-cols-3 gap-8">
              {vaeFeatures.map((feature, index) => (
                <div 
                  key={index} 
                  className="group bg-card rounded-xl p-8 hover:shadow-xl transition-all duration-300 border border-border hover:-translate-y-1"
                >
                  <h3 className="text-xl font-medium text-brand-blue dark:text-brand-orange group-hover:text-[#8B5CF6] transition-colors mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-16 text-center">
              <Link
                to="/contact?type=demo"
                className="inline-flex items-center px-8 py-4 rounded-lg bg-gradient-to-r from-brand-blue to-brand-orange text-white hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl text-lg"
              >
                {t('vae.cta')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Recruiters;
